"use client"

import { usePathname, useRouter } from "next/navigation"
import { useState } from "react"
import { Button } from "@/components/ui/button"

import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer"
import { ScrollArea } from "@/components/ui/scroll-area"
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

import { menu_data } from "@/data/menu"

const CATEGORY_SLUG_REGEX = /^\/categorias\/([^/]+)\/?$/

const getCategorySlugFromPathname = (pathname: string | null) => {
  const match = pathname?.match(CATEGORY_SLUG_REGEX)
  return match?.[1] ? decodeURIComponent(match[1]) : undefined
}

interface CategorySelectDrawerContentProps {
  onSelect?: () => void
}

export function CategorySelectDrawerContent({
  onSelect,
}: CategorySelectDrawerContentProps) {
  const router = useRouter()
  const pathname = usePathname()

  const selectedCategorySlug = getCategorySlugFromPathname(pathname) ?? ""

  const handleNavigate = (slug: string) => {
    const category = menu_data.find((category) => category.slug === slug)
    if (!category) return

    router.push(category.url)
    onSelect?.()
  }

  return (
    <ScrollArea className='min-h-0 flex-1 px-4 pb-6'>
      <nav aria-label='Categorias' className='flex flex-col'>
        {menu_data.map((category) => {
          const isActive = category.slug === selectedCategorySlug
          return (
            <button
              aria-current={isActive ? "true" : undefined}
              className={`border-b py-4 text-left text-base transition-colors last:border-b-0 ${isActive ? "font-medium text-primary" : "text-foreground/80"}
                      `}
              key={category.id}
              onClick={() => handleNavigate(category.slug)}
              type='button'
            >
              {category.name}
            </button>
          )
        })}
      </nav>
    </ScrollArea>
  )
}

export function CategorySelect() {
  const router = useRouter()
  const pathname = usePathname()

  const [open, setOpen] = useState(false)

  const selectedCategorySlug = getCategorySlugFromPathname(pathname)

  const handleNavigate = (slug: string) => {
    const category = menu_data.find((category) => category.slug === slug)
    if (!category) return

    router.push(category.url)
    setOpen(false)
  }

  return (
    <>
      <div className='hidden md:block'>
        <Select onValueChange={handleNavigate} value={selectedCategorySlug}>
          <SelectTrigger aria-label='Selecionar categoria'>
            <div className='sr-only'>
              <SelectValue placeholder='Produtos' />
            </div>
            <span aria-hidden='true'>Produtos</span>
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectLabel id='category-select-label'>Categoria</SelectLabel>
              {menu_data.map((category) => (
                <SelectItem
                  aria-current={
                    category.slug === selectedCategorySlug ? "true" : undefined
                  }
                  key={category.id}
                  value={category.slug}
                >
                  {category.name}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>

      <div className='md:hidden'>
        <Drawer onOpenChange={setOpen} open={open}>
          <DrawerTrigger asChild>
            <Button
              aria-haspopup='dialog'
              className='w-full justify-start'
              variant='outline'
            >
              Produtos
            </Button>
          </DrawerTrigger>

          <DrawerContent className='flex h-[85vh] flex-col'>
            <DrawerHeader className='shrink-0'>
              <DrawerTitle>Categorias</DrawerTitle>
            </DrawerHeader>

            <CategorySelectDrawerContent onSelect={() => setOpen(false)} />
          </DrawerContent>
        </Drawer>
      </div>
    </>
  )
}
